import React, { useState, useEffect } from "react";
import { Checkbox } from "antd";
import threeDotsIcon from "../../assets/three_dots_sq.svg";
import closeIcon from "../../assets/close__s__stroke.svg";
import CustomButton from "../Atoms/Buttons/CustomButton";
import { useDashboard } from "../../contexts/DashboardContext";
import UploadDragger from "./UploadDragger";
import ProcessedData from "./ProcessedData";
import { TOAST_CLASSES } from "../../utils/constants";
import "./css/upload.content.css";

// Mock data for uploaded solutions
const fileMockData = [
  {
    solName: "Solution 1",
    status: "Draft",
    comment: "This is a sample comment",
  },
  {
    solName: "Solution 2",
    status: "Draft",
    comment: "This is another sample comment",
  },
  {
    solName: "Solution 3",
    status: "Draft",
    comment: "This is yet another sample comment",
  },
];

const UploadContent = ({ type }) => {
  const { showToastMessage, uploadDrawerOpen } = useDashboard();

  const [publishAllChecked, setPublishAllChecked] = useState(false);

  const [file, setFile] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hideProgressBar, setHideProgressBar] = useState(false);

  const [showUploadArea, setShowUploadArea] = useState(true);

  // Clear data when drawer opens
  useEffect(() => {
    if (uploadDrawerOpen) {
      setFile(null);
      setShowSuccessMessage(false);
      setHideProgressBar(false);
      setPublishAllChecked(false);
    }
  }, [uploadDrawerOpen]);

  // Show error toast if type is missing - use useEffect to avoid setState during render
  useEffect(() => {
    if (!type) {
      showToastMessage("Failed to open Upload Section!", TOAST_CLASSES?.ERROR);
    }
  }, [type, showToastMessage]);

  // Handle submit button click - after file upload
  const handleSubmit = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file.originFileObj || file);

    try {
      const response = await fetch(
        "https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Upload successful:", result);
        // Don't change file state here - just log success
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  // This function is called when a file is uploaded
  const onFileUpload = (uploadedFile) => {
    setFile(uploadedFile);

    // successful upload
    if (uploadedFile.status === "done") {
      setShowSuccessMessage(true);

      // Hide progress bar after upload is complete
      setTimeout(() => {
        setHideProgressBar(true);
      }, 1000);
    }
    // Error in upload
    else if (uploadedFile.status === "error") {
      setShowSuccessMessage(false);

      // Hide progress bar after upload is complete
      setTimeout(() => {
        setHideProgressBar(true);
      }, 1000);
    }
    // Handle uploading state
    else {
      setShowSuccessMessage(false);
      setHideProgressBar(false);
    }
  };

  // Don't render anything if type is missing
  if (!type) {
    return null;
  }

  return (
    <article className="marketplace-upload-container">
      {showUploadArea && (
        <section>
          {/* Upload Dragger */}
          <section className="upload-file-area">
            <UploadDragger handleFileUpload={onFileUpload} />
          </section>

          {/* File Status */}
          {file && (
            <section
              className="uploaded-file-status mt-4"
              style={{
                borderBottom:
                  file.status === "done"
                    ? "1px solid var(--db-secondary-gray-four)"
                    : "none",
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <img src={threeDotsIcon} alt="file" width={24} height={24} />
                  <p>{file?.name || ""}</p>
                </div>

                <img
                  onClick={() => file.status !== "uploading" && setFile(null)}
                  style={{
                    cursor:
                      file.status === "uploading" ? "not-allowed" : "pointer",
                  }}
                  src={closeIcon}
                  alt="close"
                  width={14}
                  height={14}
                />
              </div>

              {/* Progress Bar */}
              <div
                className="progress-bar"
                style={{ display: hideProgressBar ? "none" : "block" }}
              >
                <div
                  className={`progress-bar-inner${
                    file.status === "error" ? " error" : ""
                  }`}
                  style={{
                    width: `${file.percent || 0}%`,
                    background:
                      file.status === "error"
                        ? "var(--db-semantic-red)"
                        : "var(--db-primary-accessible-green)",
                    height: "100%",
                    transition: "width 0.3s ease",
                  }}
                ></div>
              </div>

              {/* File Size */}
              <div className="file-size mb-1 mt-1">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </div>

              {/* Upload Status Messages */}
              {file.status === "done" && showSuccessMessage && (
                <div className="upload-final-status">Successfully uploaded</div>
              )}

              {file.status === "error" && (
                <div
                  className="upload-final-status error"
                  style={{ color: "var(--db-semantic-red)" }}
                >
                  Upload failed
                </div>
              )}

              {file.status === "uploading" && (
                <div className="upload-final-status uploading">
                  Uploading...
                </div>
              )}
            </section>
          )}

          {/* Publish All Checkbox */}
          <div className="publish-all-check mb-2 mt-5 ps-2">
            <Checkbox
              checked={publishAllChecked}
              onChange={(e) => setPublishAllChecked(e.target.checked)}
            >
              {"Publish Directly"}
            </Checkbox>
          </div>

          {/* Submit Button */}
          <div className="d-flex justify-content-end mt-5">
            <CustomButton
              label="Submit"
              clsNames="mas-secondary-btn-3 mas-btn-med"
              disabled={!file || file.status === "uploading"}
              onClickFn={handleSubmit}
            />
          </div>
        </section>
      )}

      {showUploadArea && <ProcessedData data={fileMockData} />}
    </article>
  );
};

export default UploadContent;
